import { PageHeader } from '../../components/ui/PageHeader.tsx';
import { PageActions } from '../../components/ui/PageActions.tsx';
import { Link } from 'react-router';
import { useMe } from '../../lib/auth.tsx';
import { CYCLE_LABEL, STATE_LABEL, type EvidenceItem } from '../../lib/evidence.ts';
import { useGridQuery, useGridState } from '../../lib/grid.ts';
import { DataTable, type Column } from '../../components/DataTable.tsx';
import { Badge, LinkButton } from '../../components/ui/Button.tsx';
import { can } from '../../lib/perm.ts';
import { fmtDate } from '../../lib/date.ts';

const STATE_TONE: Record<string, 'success' | 'info' | 'danger' | 'neutral'> = { valid: 'success', expiring: 'info', expired: 'danger', missing_period: 'danger', none: 'neutral' };
type Row = EvidenceItem & { ownerName: string; latestVersionNo: number | null; validUntil: string | null; capturedAt: string | null; controlCount: number };

const columns: Column<Row>[] = [
  { key: 'title', label: '항목', filter: 'text', render: (i) => <><Link className="text-accent-strong hover:underline" to={`/evidence/items/${i.id}`}>{i.title}</Link>{!i.isActive && <span className="ml-1 text-[11px] text-slate-400">(비활성)</span>}</> },
  { key: 'ownerName', label: '담당자', width: '7rem', filter: 'text' },
  { key: 'cycle', label: '주기', width: '7rem', filter: 'select', render: (i) => <>{CYCLE_LABEL[i.cycle]}{i.currentPeriod && <span className="text-[11px] text-slate-500 ml-1">{i.currentPeriod}</span>}</> },
  { key: 'controlCount', label: '통제항목', row: 2, span: 1, align: 'right', render: (i) => `${i.controlCount}개` },
  { key: 'state', label: '상태', width: '8rem', filter: 'select', render: (i) => <Badge tone={STATE_TONE[i.state]}>{STATE_LABEL[i.state]}</Badge> },
  { key: 'latestVersionNo', label: '최신 승인', row: 2, span: 2, at: 1, render: (i) => <span className="text-[12px]">{i.latestApproved ? `v${i.latestApproved.versionNo} · ${fmtDate(i.latestApproved.capturedAt)}` : '—'}</span> },
  { key: 'validUntil', label: '유효기간', width: '7rem', filter: 'date', render: (i) => <span className="text-[12px]">{i.validUntil ? fmtDate(i.validUntil) : i.latestApproved ? '무기한' : '—'}</span> },
  { key: 'isActive', label: '활성', row: 2, span: 1, at: 3, filter: 'bool', align: 'center', render: (i) => i.isActive ? '예' : '아니오' },
  { key: 'pending', label: '검토 중', width: '7rem', sortable: false, render: (i) => <span className="text-[12px]">{i.pending ? `v${i.pending.versionNo} ${i.pending.status}` : ''}</span> },
];

/** 증적 항목 목록 + 새 항목 — 서버사이드 그리드 */
export function EvidenceItemsPage() {
  const me = useMe();
  const { state, set } = useGridState('e', { sort: { col: 'title', dir: 'asc' } });
  const items = useGridQuery<Row>('evidence-grid', '/evidence/items/grid', state);
  const canManage = can(me.data, 'evidence.manage');

  return (
    <div>
      <PageHeader title="증적 관리" />
      {items.isError && <div className="text-red-600 text-sm mb-2">{(items.error as Error).message}</div>}
      <DataTable id="evidence-items" columns={columns} data={items.data} state={state} onChange={set} isFetching={items.isFetching} />
      <PageActions>{canManage && <LinkButton to="/evidence/items/new">새 항목</LinkButton>}</PageActions>
    </div>
  );
}
